<template>
  <div class="page-wrapper">
    <div class="card bg-white border-0 rounded-3 mb-1">
      <div class="card-body p-0">
        <!-- Селект місяць + рік -->
        <div class="month-select-wrapper">
          <select v-model="selectedMonthYear" @change="handleMonthChange" class="custom-select">
            <option v-for="(option, index) in monthOptions" :key="index" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="calendar-wrapper">
          <div class="calendar-center-frame"></div>

          <div class="horizontal-calendar-container" ref="calendarScroll" @scroll.passive="handleScrollEnd">
            <div class="horizontal-calendar" ref="calendarContainer">
              <div
                  v-for="day in visibleDays"
                  :key="day.date"
                  class="calendar-day"
                  :class="{ 'active': isActiveDay(day.date) }"
                  @click="selectDay(day.date)"
              >
                <div class="day-name">{{ day.dayName }}</div>
                <div class="day-number">{{ day.dayNumber }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DayTasks",
  data() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return {
      selectedDate: today,
      visibleDays: [],
      selectedMonthYear: "",
      scrollTimeout: null,
      monthOptions: [],
    };
  },
  mounted() {
    this.generateVisibleDays();
    this.generateMonthOptions();
    this.selectedMonthYear = this.getMonthYearValue(this.selectedDate);
    this.$nextTick(() => this.scrollToDate(this.selectedDate, false));
  },
  methods: {
    generateVisibleDays() {
      const centerDate = new Date();
      centerDate.setHours(0, 0, 0, 0);
      const days = [];

      for (let i = -400; i <= 400; i++) {
        const date = new Date(centerDate);
        date.setDate(centerDate.getDate() + i);

        days.push({
          date,
          dayName: date.toLocaleDateString("ru-RU", { weekday: "short" }),
          dayNumber: date.getDate(),
        });
      }

      this.visibleDays = days;
    },
    generateMonthOptions() {
      const options = new Set();

      this.visibleDays.forEach(({ date }) => {
        const value = this.getMonthYearValue(date);
        const label = date.toLocaleDateString("ru-RU", {
          month: "long",
          year: "numeric",
        }).replace(/^./, c => c.toUpperCase());

        options.add(JSON.stringify({ value, label }));
      });

      this.monthOptions = Array.from(options).map(item => JSON.parse(item));
    },
    getMonthYearValue(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    },
    scrollToDate(date, smooth = true) {
      const container = this.$refs.calendarScroll;
      const index = this.visibleDays.findIndex(
          d => d.date.toDateString() === date.toDateString()
      );
      if (index === -1 || !container) return;

      const dayWidth = 56;
      const scrollX = index * dayWidth - container.clientWidth / 2 + dayWidth / 2;
      container.scrollTo({ left: scrollX, behavior: smooth ? "smooth" : "auto" });
    },
    handleMonthChange() {
      const [year, month] = this.selectedMonthYear.split("-");
      const targetDate = new Date(year, month - 1, 1);
      targetDate.setHours(0, 0, 0, 0);
      this.selectedDate = targetDate;
      this.scrollToDate(targetDate, true);
    },
    handleScrollEnd() {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        const container = this.$refs.calendarScroll;
        const calendar = this.$refs.calendarContainer;
        const containerRect = container.getBoundingClientRect();
        const centerX = containerRect.left + container.clientWidth / 2;

        let closestDay = null;
        let minDistance = Infinity;

        Array.from(calendar.children).forEach((child, index) => {
          const rect = child.getBoundingClientRect();
          const center = rect.left + rect.width / 2;
          const dist = Math.abs(centerX - center);

          if (dist < minDistance) {
            minDistance = dist;
            closestDay = this.visibleDays[index];
          }
        });

        if (closestDay) {
          this.selectedDate = closestDay.date;
          this.selectedMonthYear = this.getMonthYearValue(closestDay.date);
        }
      }, 100);
    },
    selectDay(date) {
      this.selectedDate = date;
      this.selectedMonthYear = this.getMonthYearValue(date);
      this.scrollToDate(date, true);
    },
    isActiveDay(date) {
      return date.toDateString() === this.selectedDate.toDateString();
    },
  },
};
</script>

<style scoped>
.page-wrapper {
  overflow: hidden;
}

.month-select-wrapper {
  text-align: center;
  margin-top: 12px;
  margin-bottom: 4px;
}

.custom-select {
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
  background-color: #f8f9fa;
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg fill='gray' height='20' viewBox='0 0 24 24' width='20' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>");
  background-repeat: no-repeat;
  background-position-x: calc(100% - 12px);
  background-position-y: center;
  background-size: 16px;
}

.calendar-wrapper {
  position: relative;
  padding: 12px 0;
}

.calendar-center-frame {
  position: absolute;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  width: 48px;
  height: 64px;
  border-radius: 12px;
  pointer-events: none;
  z-index: 10;
}

.horizontal-calendar-container {
  overflow-x: auto;
  white-space: nowrap;
  padding: 0 12px;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

.horizontal-calendar-container::-webkit-scrollbar {
  display: none;
}

.horizontal-calendar {
  display: inline-flex;
  gap: 8px;
  padding: 8px 0;
}

.calendar-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 64px;
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.calendar-day.active {
  font-weight: 600;
  color: #0d6efd;
  transform: scale(1.5);
}

.day-name {
  font-size: 12px;
  opacity: 0.8;
  text-transform: capitalize;
}

.day-number {
  font-size: 18px;
  font-weight: 600;
}
</style>
